import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { TrendingDown, TrendingUp, AlertTriangle, CheckCircle, Calendar, ArrowRight, ArrowLeft, FileText } from 'lucide-react';
import axios from 'axios';
import CustomerNavigation from '@/components/CustomerNavigation';
import SEO from '@/components/SEO';

export default function CustomerBulkHistory() {
  const [bulkRegistrations, setBulkRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBulkHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bulk-registrations`, { withCredentials: true });
      if (response.data && response.data.length > 0) {
        const registrations = response.data
          .map(reg => ({
            id: reg.id,
            date: reg.date,
            area_name: reg.area_name || 'Ukjent omrade',
            biologist: reg.biologist || reg.created_by || 'Biolog',
            sample_count: reg.sample_count || 0,
            status: reg.status || 'normal',
            title: reg.ai_summary ? `Planktonanalyse - ${reg.area_name}` : `Prove ${reg.date}`,
            summary: reg.ai_summary || 'Ingen sammendrag tilgjengelig'
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setBulkRegistrations(registrations);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bulk history:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBulkHistory(); }, [fetchBulkHistory]);

  const getStatusConfig = (status) => {
    const configs = {
      critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', icon: AlertTriangle, label: 'Raud' },
      sent: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: CheckCircle, label: 'Sendt' },
      monitor: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', icon: TrendingUp, label: 'Overvaaking' },
    };
    return configs[status] || { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: TrendingDown, label: 'Grøn' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex">
        <CustomerNavigation />
        <main className="flex-1 min-h-screen">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500 text-sm">Laster registreringer...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      <SEO title="Historikk" description="Historiske bulk-registreringer" />
      <CustomerNavigation />
      <main className="flex-1 min-h-screen">
        <div className="max-w-6xl mx-auto px-6 py-8 pt-16 lg:pt-8 animate-fade-in">
          
          {/* Back + Header */}
          <div className="mb-8">
            <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" />
              Tilbake til dashboard
            </Link>
            <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight mb-2" data-testid="bulk-history-title">
              Bulk Registreringer
            </h1>
            <p className="text-slate-500">Detaljerte proveresultater fra laboratoriet</p>
          </div>

          {/* List */}
          {bulkRegistrations.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Ingen bulk registreringer tilgjengelig</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bulkRegistrations.map((bulk, i) => {
                const sc = getStatusConfig(bulk.status);
                const StatusIcon = sc.icon;
                return (
                  <div 
                    key={bulk.id}
                    data-testid={`bulk-card-${bulk.id}`}
                    className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-sky-200 hover:shadow-sm transition-all group"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-xl ${sc.bg} flex items-center justify-center flex-shrink-0`}>
                          <StatusIcon className={`w-5 h-5 ${sc.text}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-heading font-semibold text-slate-900 truncate">{bulk.title}</h3>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${sc.bg} ${sc.text} ${sc.border}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                              {sc.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-5 mb-3 text-sm text-slate-500">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{new Date(bulk.date).toLocaleDateString('nb-NO')}</span>
                            </div>
                            <span>{bulk.area_name}</span>
                            <span className="font-mono text-xs">{bulk.sample_count} stasjonar</span>
                          </div>
                          <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                            {bulk.summary}
                          </p>
                        </div>
                      </div>
                      <Link to={`/dashboard/bulk-view/${bulk.id}`} className="flex-shrink-0">
                        <button className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 hover:scale-[1.02] active:scale-[0.98] transition-all">
                          Se detaljer
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
